import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from 'css/pages/student-live.module.css'
import { ConnectingPage, PayAttentionPage, TeacherInterruptionPage } from 'components/presentation-status-page'
import { verifyLogin } from 'util/cache'
import { useHighContrast, HighContrastToggle } from 'components/high-contrast-toggle'
import { useSpeech } from 'components/speech'
import { useStudentId } from 'student-live/use-student-id'
import { useStudentLiveConnection } from 'student-live/use-student-live-connection'
import { useStudentAnswers } from 'student-live/use-student-answers'
import { StudentQuestionView } from 'student-live/student-question-view'
import { StudentDoneView } from 'student-live/student-done-view'
import { EvictedView, UnreachableView } from 'student-live/connection-trouble-view'

export const StudentLivePage: React.FC = () => {
	const { highContrast, toggleHighContrast } = useHighContrast()
	const speech = useSpeech(0.95)
	const stopSpeech = speech.stop
	const navigate = useNavigate()

	const studentId = useStudentId()
	const connection = useStudentLiveConnection()
	const {
		status, setStatus, questions, currentIndex, setCurrentIndex, learningStage,
		classId, lessonId, connectionState, sessionEndedTick,
	} = connection

	const studentAnswers = useStudentAnswers({
		studentId,
		lessonId,
		classId,
		questions,
		currentIndex,
		learningStage,
		sessionEndedTick,
		navigate,
		setStatus,
	})

	useEffect(() => {
		verifyLogin(navigate)
	})

	/* Stop read-aloud when the student moves to the next question, so the old
	   question isn't still being read over the new one. */
	useEffect(() => { stopSpeech() }, [currentIndex, stopSpeech])

	const handleAdvance = () => {
		if (currentIndex < questions.length - 1) {
			setCurrentIndex(currentIndex + 1)
		}
	}

	const renderContent = () => {
		if (connectionState.closeReason === 'evicted') {
			return <EvictedView />
		}
		if (connectionState.status === 'failed' || connectionState.closeReason === 'auth') {
			return <UnreachableView />
		}
		switch (status) {
			case 'connecting':
				return <ConnectingPage
					isReconnecting={connectionState.status === 'reconnecting'}
					reconnectAttempt={connectionState.reconnectAttempt}
					maxAttempts={10}
				/>
			case 'waiting':
				return <PayAttentionPage />
			case 'interrupt':
				return <TeacherInterruptionPage />
			case 'done':
				return <StudentDoneView submitState={studentAnswers.submitState} onRetry={studentAnswers.retrySubmit} />
			case 'questions': {
				const q = questions[currentIndex]
				if (!q) return <PayAttentionPage />
				return (
					<StudentQuestionView
						question={q}
						currentIndex={currentIndex}
						totalQuestions={questions.length}
						learningStage={learningStage}
						answer={studentAnswers.getAnswer(q.id)}
						speech={speech}
						ensureInitializedAnswer={studentAnswers.ensureInitializedAnswer}
						onAnswerChange={studentAnswers.handleAnswerChange}
						onAdvance={handleAdvance}
						onDone={studentAnswers.handleDone}
					/>
				)
			}
			default:
				return null
		}
	}

	return (
		<div className={styles.studentLiveContainer}>
			{/* Per-device, not driven by the teacher's board: a student who needs
			    high contrast needs it whether or not the teacher turned it on. */}
			<div className={styles.accessibilityBar}>
				<HighContrastToggle on={highContrast} onToggle={toggleHighContrast} />
			</div>
			{renderContent()}
		</div>
	)
}
